<template>
  <div v-if="isHKlandUser || canTenantRead">
    <HKLTabs
      v-if="showFitout"
      :activeKey="myActiveKey"
      class="submissions-entry"
      @change="tabPaneChange"
    >
      <a-tab-pane
        key="1"
      >
        <template slot="tab">
          <span>
            {{ $t('tenantProjects.FitoutComment') }}
          </span>
          <div
            v-if="showSubmissionPin"
            class="pin__bubble-orange tab_pin"
          >
            <i
              class="fa fa-thumb-tack"
              aria-hidden="true"
            />
          </div>
          <div
            v-if="showFitoutDoc"
            class="tab-new-doc"
          >
            {{ $t('tenantProjects.docs') }}
          </div>
        </template>
        <Submissions />
      </a-tab-pane>
      <a-tab-pane
        key="2"
        class="submission-tab"
      >
        <template slot="tab">
          <span>
            {{ $t('tenantProjects.postFitout.title') }}
          </span>
          <div
            v-if="showLetterPin"
            class="pin__bubble-orange tab_pin"
          >
            <i
              class="fa fa-thumb-tack"
              aria-hidden="true"
            />
          </div>
        </template>
        <PostFitOutEntry />
      </a-tab-pane>
      <a-tab-pane
        v-if="enableGreenFitOutFeature"
        key="3"
      >
        <template slot="tab">
          <span>
            {{ $t('tenantProjects.greenFitOut.tabTitle') }}
          </span>
          <div
            v-if="showGreenFitoutPin"
            class="pin__bubble-orange tab_pin"
          >
            <i
              class="fa fa-thumb-tack"
              aria-hidden="true"
            />
          </div>
          <div
            v-if="showGreenFitoutDoc"
            class="tab-new-doc"
          >
            {{ $t('tenantProjects.docs') }}
          </div>
        </template>
        <GreenFitOutEntry />
      </a-tab-pane>
    </HKLTabs>

    <div
      v-if="showReinstatement"
      class="submissions-entry"
    >
      <Submissions />
    </div>
  </div>
  <p v-else>
    {{ $t('noTabPermission') }}
  </p>
</template>

<script>
import { computed, onMounted, ref, provide, unref, onUnmounted, watch } from 'vue-demi'
import EventBus from '@/utils/eventBus'
import HKLTabs from '@/controls/Tab/HKLTabs'
import { useStoreGetters } from '@/store'
import { showDocForGreenFitout, showDocForSubmission } from '@/modules/tenantProjects/utils'
import { tenantProjectTypes } from '@/utils/locale'
import useRouter from '@/router/useRouter'
import { DEPENDENCY_KEYS, TAB_TYPE } from './consts'
import { useTenantProjectSubmissionPins } from './hook'
import Submissions from './submissions/Submissions'
import PostFitOutEntry from './postFitOutLetter/PostFitOutEntry'
import GreenFitOutEntry from './greenFitOut/GreenFitOutEntry'

function resolveSubmissionActiveKey (activeKey, enableGreenFitOutFeature) {
  const key = activeKey || '1'
  if (key === '3' && !enableGreenFitOutFeature) {
    return '1'
  }
  return key
}

export default {
  name: 'SubmissionsEntry',
  components: {
    HKLTabs,
    Submissions,
    PostFitOutEntry,
    GreenFitOutEntry,
  },
  setup () {
    const { route, router } = useRouter()
    const { currentTenantProject, canTenantRead } = useStoreGetters('tenantProject', ['currentTenantProject', 'canTenantRead'])
    const { isHKlandUser } = useStoreGetters('user', ['isHKlandUser'])
    const regionStore = useStoreGetters('regions', ['enableGreenFitOutFeature'])
    const { enableGreenFitOutFeature } = regionStore
    const { greenFitout, showSubmissionPin, showLetterPin, showGreenFitoutPin } = useTenantProjectSubmissionPins()

    const showGreenFitoutDoc = computed(() => (isHKlandUser.value && showDocForGreenFitout(greenFitout.value)))
    const showFitoutDoc = computed(() => (isHKlandUser.value && showDocForSubmission(currentTenantProject.value)))
    const myActiveKey = ref(resolveSubmissionActiveKey(
      unref(route).query.submissionActiveKey,
      unref(enableGreenFitOutFeature),
    ))

    const syncSubmissionActiveKeyFromRoute = () => {
      const rawKey = unref(route).query.submissionActiveKey
      const safeKey = resolveSubmissionActiveKey(rawKey, unref(enableGreenFitOutFeature))
      myActiveKey.value = safeKey
      if (rawKey === '3' && safeKey === '1') {
        unref(router).replace({
          path: unref(route).path,
          query: { ...unref(route).query, submissionActiveKey: '1' },
        })
      }
    }

    watch(() => unref(route).query.submissionActiveKey, syncSubmissionActiveKeyFromRoute)
    watch(enableGreenFitOutFeature, syncSubmissionActiveKeyFromRoute)

    onMounted(async () => {
      syncSubmissionActiveKeyFromRoute()
      EventBus.on('submission-sub-tab-change', (activeKey) => {
        tabPaneChange(activeKey)
      })
    })
    onUnmounted(() => {
      EventBus.off('submission-sub-tab-change')
    })

    const tabType = computed(() => {
      const map = {
        '1': TAB_TYPE.FIT_OUT,
        '2': TAB_TYPE.POST_FIT_OUT,
        '3': TAB_TYPE.GREEN_FIT_OUT,
      }
      return map[unref(myActiveKey)]
    })
    provide(DEPENDENCY_KEYS.TAB_TYPE, tabType)
    const tabPaneChange = (activeKey) => {
      myActiveKey.value = resolveSubmissionActiveKey(activeKey, unref(enableGreenFitOutFeature))
    }
    const showFitout = computed(() => {
      return currentTenantProject.value.type === tenantProjectTypes.Fitout
    })
    const showReinstatement = computed(() => {
      return currentTenantProject.value.type === tenantProjectTypes.Reinstatement
    })
    return {
      ...regionStore,
      isHKlandUser,
      myActiveKey,
      tabPaneChange,
      currentTenantProject,
      showLetterPin,
      showSubmissionPin,
      showGreenFitoutPin,
      showFitout,
      showReinstatement,
      canTenantRead,
      showGreenFitoutDoc,
      showFitoutDoc,
    }
  },
}
</script>
<style lang="scss" scoped>
.submissions-entry {
  overflow: initial;
    .tab_pin {
      width:30px;
      height: 17px;
      margin-left: 6px;
      margin-top:7px;
    }

    .tab-new-doc {
      margin-left:10px;
      margin-top: 7px;
    }

  ::v-deep .submissions__topbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 10px;
    & .btn:not(:last-child) {
      margin-right: 5px;
    }
  }
}
</style>
